/**
 * PWA Installation Guide Step
 * Shows platform-specific installation instructions
 */

import { Smartphone, Share, Menu, Download } from 'lucide-react';
import { OnboardingStep } from '../OnboardingStep';
import { useEffect, useState } from 'react';

type Platform = 'ios' | 'android' | 'desktop';

function detectPlatform(): Platform {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  const isAndroid = /android/.test(userAgent);

  if (isIOS) return 'ios';
  if (isAndroid) return 'android';
  return 'desktop';
}

export function PWAInstallStep() {
  const [platform, setPlatform] = useState<Platform>('desktop');

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  const getInstallationInstructions = () => {
    switch (platform) {
      case 'ios':
        return {
          title: 'ติดตั้งบน iOS',
          steps: [
            { icon: Share, text: '1. กดปุ่ม Share ด้านล่าง' },
            { icon: Download, text: '2. เลือก "Add to Home Screen"' },
            { icon: Smartphone, text: '3. กดยืนยันการติดตั้ง' },
          ],
        };
      case 'android':
        return {
          title: 'ติดตั้งบน Android',
          steps: [
            { icon: Menu, text: '1. กดเมนูมุมขวาบน (⋮)' },
            { icon: Download, text: '2. เลือก "Add to Home screen"' },
            { icon: Smartphone, text: '3. กดยืนยันการติดตั้ง' },
          ],
        };
      default:
        return {
          title: 'ติดตั้งบน Desktop',
          steps: [
            { icon: Download, text: '1. มองหาไอคอน Install ในแถบ URL' },
            { icon: Smartphone, text: '2. กดปุ่ม Install' },
            { icon: Menu, text: '3. เปิดใช้งานจากเมนู' },
          ],
        };
    }
  };

  const instructions = getInstallationInstructions();

  return (
    <OnboardingStep
      title="ติดตั้งเป็นแอป"
      description="ติดตั้ง Quran Assistant เป็นแอปบนอุปกรณ์ของคุณ เพื่อเข้าถึงได้ง่ายและรับการแจ้งเตือนจากเรา"
      illustration={
        <div className="w-full max-w-sm space-y-6">
          {/* Platform Badge */}
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
              <Smartphone className="w-4 h-4" />
              {instructions.title}
            </div>
          </div>

          {/* Installation Steps */}
          <div className="space-y-4">
            {instructions.steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={index}
                  className="flex items-start gap-4 bg-card border border-border rounded-xl p-4"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-sm text-foreground pt-2">{step.text}</p>
                </div>
              );
            })}
          </div>

          {/* Optional Note */}
          <p className="text-xs text-center text-muted-foreground">
            คุณสามารถข้ามขั้นตอนนี้และติดตั้งทีหลังได้
          </p>
        </div>
      }
    />
  );
}

export default PWAInstallStep;
